import { ShrinkOutlined, ExpandAltOutlined } from "@ant-design/icons";
import { Modal, Progress, Spin } from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import type { GridDiffDifference, GridDiffStatus } from "./types";

export type CompareResultsStatus = "loading" | "done" | "error";

export interface CompareResultsModalProps {
  open: boolean;
  onClose: () => void;
  fileAName: string;
  fileBName: string;
  status: CompareResultsStatus;
  snapshot: GridDiffStatus | null;
  errorMessage: string | null;
}

type CompareTab = "overlay" | "sidebyside";

interface ViewerChange {
  id: number;
  cell: string;
  bbox_norm: [number, number, number, number];
  category: string;
  description: string;
  before: string;
  after: string;
  source: "cluster" | "residual";
}

const CLUSTER_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#06b6d4",
  "#3b82f6",
  "#a855f7",
  "#ec4899"
];

const colorForId = (id: number) => CLUSTER_COLORS[id % CLUSTER_COLORS.length];

const toViewerChange = (
  diff: GridDiffDifference,
  fallbackIndex: number,
  source: "cluster" | "residual",
  gridInfo: GridDiffStatus["grid_info"]
): ViewerChange => {
  const imageWidth = Number(gridInfo.image_width || 1);
  const imageHeight = Number(gridInfo.image_height || 1);
  const union = diff.union_cell_bbox ||
    diff.absolute_bbox || [0, 0, imageWidth, imageHeight];
  const [x0, y0, x1, y1] = union;
  const w = Math.max(1, x1 - x0);
  const h = Math.max(1, y1 - y0);
  return {
    id:
      source === "residual"
        ? 1000 + fallbackIndex + 1
        : Number(diff.box_id || fallbackIndex + 1),
    cell:
      diff.grid ||
      (source === "residual"
        ? `S${fallbackIndex + 1}`
        : `#${fallbackIndex + 1}`),
    bbox_norm: [
      x0 / imageWidth,
      y0 / imageHeight,
      w / imageWidth,
      h / imageHeight
    ],
    category: diff.category || "change",
    description: diff.description || "",
    before: diff.before || "",
    after: diff.after || "",
    source
  };
};

/**
 * Pan / zoom / SVG-overlay panel — shared by both tabs.
 *
 * `src` may be `null` until the API returns it; we render a spinner instead
 * of an `<img>` so the browser doesn't fire a broken-image error.
 */
const ImagePanel: React.FC<{
  src: string | null;
  label: string;
  changes: ViewerChange[];
  selected: ViewerChange | null;
  onSelect: (change: ViewerChange | null) => void;
  height: string;
}> = ({ src, label, changes, selected, onSelect, height }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    sx: number;
    sy: number;
    ox: number;
    oy: number;
  } | null>(null);
  const prevSrc = useRef<string | null>(null);

  const [nat, setNat] = useState({ w: 1, h: 1 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const fit = useCallback((w: number, h: number) => {
    const el = containerRef.current;
    if (!el || w <= 1) return;
    const z = Math.min(el.clientWidth / w, el.clientHeight / h) * 0.97;
    setZoom(z);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Re-fit on container resize.
  useEffect(() => {
    if (nat.w <= 1) return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => fit(nat.w, nat.h));
    ro.observe(el);
    return () => ro.disconnect();
  }, [nat, fit]);

  // Non-passive wheel — prevents the browser from zooming the whole page.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setZoom((z) =>
        Math.min(20, Math.max(0.04, z * (e.deltaY < 0 ? 1.12 : 0.89)))
      );
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Pan to the selected change.
  useEffect(() => {
    if (!selected || nat.w <= 1) return;
    const [nx, ny, nw, nh] = selected.bbox_norm;
    const el = containerRef.current;
    if (!el) return;
    setOffset({
      x: el.clientWidth / 2 - (nx + nw / 2) * nat.w * zoom,
      y: el.clientHeight / 2 - (ny + nh / 2) * nat.h * zoom
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (prevSrc.current !== src) {
      prevSrc.current = src;
      setNat({ w, h });
      fit(w, h);
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      ox: offset.x,
      oy: offset.y
    };
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.ox + e.clientX - dragRef.current.sx,
      y: dragRef.current.oy + e.clientY - dragRef.current.sy
    });
  };
  const onMouseUp = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  const imgW = nat.w * zoom;
  const imgH = nat.h * zoom;

  return (
    <div
      ref={containerRef}
      className="relative bg-gray-900 rounded-lg overflow-hidden"
      style={{
        height,
        cursor: isDragging ? "grabbing" : src ? "grab" : "default"
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <span className="absolute top-2 left-2 z-10 text-xs text-white bg-black/50 px-2 py-1 rounded">
        {label}
      </span>

      {!src && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spin />
        </div>
      )}

      {src && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: imgW,
            height: imgH,
            maxWidth: "none",
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            willChange: "transform"
          }}
        >
          <img
            src={src}
            onLoad={handleLoad}
            alt={label}
            draggable={false}
            style={{
              width: imgW,
              height: imgH,
              maxWidth: "none",
              maxHeight: "none",
              display: "block",
              pointerEvents: "none"
            }}
          />

          <svg
            viewBox={`0 0 ${nat.w} ${nat.h}`}
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: imgW,
              height: imgH,
              maxWidth: "none",
              maxHeight: "none",
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            {changes
              .filter((ch) => !selected || ch.id === selected.id)
              .map((ch) => {
                const [nx, ny, nw, nh] = ch.bbox_norm;
                const color = colorForId(ch.id);
                const rx = nx * nat.w;
                const ry = ny * nat.h;
                const rw = nw * nat.w;
                const rh = nh * nat.h;
                const labelW = Math.max(28, ch.cell.length * 9 + 20);
                return (
                  <g
                    key={ch.id}
                    style={{ pointerEvents: "all", cursor: "pointer" }}
                    onClick={() => onSelect(selected?.id === ch.id ? null : ch)}
                  >
                    <rect
                      x={rx}
                      y={ry}
                      width={rw}
                      height={rh}
                      fill={`${color}30`}
                    />
                    <rect
                      x={rx}
                      y={ry}
                      width={rw}
                      height={rh}
                      fill="none"
                      stroke={color}
                      strokeWidth={3}
                      style={{ filter: `drop-shadow(0 0 6px ${color}99)` }}
                    />
                    <rect
                      x={rx}
                      y={ry - 20}
                      width={labelW}
                      height={19}
                      rx={3}
                      fill={color}
                      opacity="0.92"
                    />
                    <text
                      x={rx + 7}
                      y={ry - 6}
                      fill="#fff"
                      fontSize={11}
                      fontWeight="700"
                      fontFamily="ui-monospace, Consolas, monospace"
                      style={{ pointerEvents: "none" }}
                    >
                      {ch.cell}
                    </text>
                  </g>
                );
              })}
          </svg>
        </div>
      )}

      <div className="absolute bottom-2 right-2 z-10 flex gap-1 bg-black/50 rounded p-1">
        <button
          onClick={() => setZoom((z) => Math.min(20, z * 1.25))}
          className="text-white text-xs px-2 py-1 hover:bg-white/10 rounded bg-transparent border-none cursor-pointer"
          title="Zoom in"
        >
          +
        </button>
        <span className="text-white text-xs px-2 py-1 min-w-[3rem] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => setZoom((z) => Math.max(0.04, z * 0.8))}
          className="text-white text-xs px-2 py-1 hover:bg-white/10 rounded bg-transparent border-none cursor-pointer"
          title="Zoom out"
        >
          −
        </button>
        <button
          onClick={() => fit(nat.w, nat.h)}
          className="text-white text-xs px-2 py-1 hover:bg-white/10 rounded bg-transparent border-none cursor-pointer"
          title="Fit to panel"
        >
          ⊡
        </button>
      </div>
    </div>
  );
};

const ChangeListItem: React.FC<{
  change: ViewerChange;
  selected: boolean;
  onClick: () => void;
}> = ({ change, selected, onClick }) => {
  const color = colorForId(change.id);
  const hasTextDiff = change.before || change.after;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-2 rounded border bg-white cursor-pointer ${
        selected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 hover:border-gray-300"
      }`}
      style={{ borderLeftWidth: 4, borderLeftColor: color }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-xs font-mono font-semibold" style={{ color }}>
          {change.cell}
        </span>
        <span className="text-xs text-gray-500 capitalize">
          {change.category.replace(/_/g, " ")}
        </span>
      </div>
      {change.description && (
        <p className="text-xs text-gray-700 m-0">{change.description}</p>
      )}
      {hasTextDiff && (
        <div className="text-xs font-mono mt-1 space-y-0.5">
          <div className="text-red-600">- {change.before || "(empty)"}</div>
          <div className="text-green-600">+ {change.after || "(empty)"}</div>
        </div>
      )}
    </button>
  );
};

export const CompareResultsModal: React.FC<CompareResultsModalProps> = ({
  open,
  onClose,
  fileAName,
  fileBName,
  status,
  snapshot,
  errorMessage
}) => {
  const [tab, setTab] = useState<CompareTab>("overlay");
  const [selected, setSelected] = useState<ViewerChange | null>(null);
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setTab("overlay");
      setMaximized(false);
    }
  }, [open]);

  // Height of the fixed-height body column. When maximized the modal content
  // is a full-viewport (100vh) flex column, so the body fills it (100%);
  // otherwise it's a fixed 74vh slice. Drives the ImagePanel + sidebar height —
  // keep them in sync (see libs/shared/ui/CLAUDE.md → "Modal layout").
  const panelHeight = maximized ? "100%" : "74vh";

  // Closing throws the comparison away, so confirm first — unless it failed
  // and there is nothing to lose.
  const handleClose = () => {
    if (status === "error") {
      onClose();
      return;
    }
    Modal.confirm({
      title: "Close compare results?",
      content:
        status === "loading"
          ? "The comparison is still running and will be cancelled."
          : "These results won't be saved. You'll need to run the comparison again to see them.",
      okText: "Close",
      okButtonProps: { danger: true },
      cancelText: "Keep open",
      centered: true,
      onOk: onClose
    });
  };

  const changes = useMemo<ViewerChange[]>(() => {
    if (!snapshot) return [];
    const cluster = (snapshot.differences || []).map((d, i) =>
      toViewerChange(d, i, "cluster", snapshot.grid_info)
    );
    const residual =
      snapshot.status === "done"
        ? (snapshot.other_differences || []).map((d, i) =>
            toViewerChange(d, i, "residual", snapshot.grid_info)
          )
        : [];
    return [...cluster, ...residual];
  }, [snapshot]);

  const primaryImage = snapshot?.grid_preview ?? null;
  const secondaryImage =
    snapshot?.grid_preview_secondary ?? snapshot?.grid_preview ?? null;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <>
          Compare Results
          {/* Anchored to `.ant-modal-content` (position:relative) at the same
              `top` as antd's close X, one slot to its left, so the two icons
              line up exactly. */}
          <button
            type="button"
            onClick={() => setMaximized((m) => !m)}
            className="flex items-center justify-center text-gray-500 hover:text-gray-800 bg-transparent border-none cursor-pointer p-0"
            style={{
              position: "absolute",
              top: 16,
              right: 56,
              width: 22,
              height: 22
            }}
            title={maximized ? "Restore" : "Maximize"}
            aria-label={maximized ? "Restore modal" : "Maximize modal"}
          >
            {maximized ? <ShrinkOutlined /> : <ExpandAltOutlined />}
          </button>
        </>
      }
      footer={null}
      width={maximized ? "100vw" : "95vw"}
      centered={!maximized}
      style={
        maximized
          ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }
          : { maxWidth: 1600 }
      }
      styles={
        maximized
          ? {
              content: {
                height: "100vh",
                borderRadius: 0,
                display: "flex",
                flexDirection: "column"
              },
              body: { flex: 1, minHeight: 0, overflow: "hidden" }
            }
          : undefined
      }
      zIndex={10000}
      destroyOnClose
    >
      {/* Fixed-height flex column: the modal stays the same size whether or
          not the loading/error banner is shown — the banner eats into the
          image area instead of growing the modal past the viewport. */}
      <div className="flex flex-col" style={{ height: panelHeight }}>
        <div className="flex items-center justify-between mb-3 gap-4 shrink-0">
          <div className="flex items-baseline gap-3 text-sm text-gray-600 min-w-0">
            <span className="truncate">
              <span className="font-medium text-gray-800">A:</span> {fileAName}
            </span>
            <span className="text-gray-300">↔</span>
            <span className="truncate">
              <span className="font-medium text-gray-800">B:</span> {fileBName}
            </span>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 shrink-0">
            {(
              [
                { key: "overlay", label: "Difference Overlay" },
                { key: "sidebyside", label: "Side by Side" }
              ] as const
            ).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-sm rounded-md border-none cursor-pointer ${
                  tab === t.key
                    ? "bg-white text-gray-900 shadow-sm font-medium"
                    : "bg-transparent text-gray-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {status === "error" && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-3 shrink-0">
            <p className="text-red-700 font-medium m-0">Comparison failed</p>
            <p className="text-red-600 text-sm m-0 mt-1">{errorMessage}</p>
          </div>
        )}

        {status === "loading" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 shrink-0">
            <div className="flex items-center gap-3">
              <Spin />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 m-0">
                  {snapshot?.message || "Preparing patch regions..."}
                </p>
                <Progress
                  percent={Math.round((snapshot?.progress ?? 0) * 100)}
                  size="small"
                  className="m-0 mt-1"
                />
              </div>
            </div>
          </div>
        )}

        <div
          className="grid gap-3 flex-1 min-h-0"
          style={{ gridTemplateColumns: "1fr 280px" }}
        >
          <div className="h-full min-h-0">
            {tab === "overlay" ? (
              <ImagePanel
                src={primaryImage}
                label="Difference Overlay"
                changes={changes}
                selected={selected}
                onSelect={setSelected}
                height="100%"
              />
            ) : (
              <div
                className="grid gap-3 h-full"
                style={{ gridTemplateColumns: "1fr 1fr" }}
              >
                <ImagePanel
                  src={primaryImage}
                  label={`A — ${fileAName}`}
                  changes={changes}
                  selected={selected}
                  onSelect={setSelected}
                  height="100%"
                />
                <ImagePanel
                  src={secondaryImage}
                  label={`B — ${fileBName}`}
                  changes={changes}
                  selected={selected}
                  onSelect={setSelected}
                  height="100%"
                />
              </div>
            )}
          </div>

          <aside className="bg-gray-50 rounded-lg p-3 overflow-y-auto h-full min-h-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-800">
                Changes ({changes.length})
              </span>
              {selected && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-xs text-blue-600 bg-transparent border-none cursor-pointer p-0"
                >
                  Clear
                </button>
              )}
            </div>
            {changes.length === 0 ? (
              <p className="text-xs text-gray-500 m-0">
                {status === "loading"
                  ? "Differences will appear as they're detected..."
                  : "No differences detected."}
              </p>
            ) : (
              <div className="space-y-2">
                {changes.map((ch) => (
                  <ChangeListItem
                    key={ch.id}
                    change={ch}
                    selected={selected?.id === ch.id}
                    onClick={() =>
                      setSelected(selected?.id === ch.id ? null : ch)
                    }
                  />
                ))}
              </div>
            )}
            {snapshot?.summary && status === "done" && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  Summary
                </p>
                <p className="text-xs text-gray-600 m-0">{snapshot.summary}</p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </Modal>
  );
};
